import { addError, addSuccess } from '@/store/reducers/ToastStore';
import { FeedbackButtons } from '@/components/chat/FeedbackButtons';
import { getChatAPI } from '@/api/ChatApi';
import { getLayoverLogger } from '@/logging/LayoverLogger';
import { Message } from '@/models/Message';
import { MESSAGE_FEEDBACK_PREFIX } from '@/analytics/props/feedback';
import { Reaction } from '@/models/Reaction';
import { RootState } from '@/store/store';
import { updateMessage } from '@/store/reducers/ChatStore';
import { useAppDispatch } from '@/store/hooks';
import { User } from '@/models/User';
import ToolsTooltip from '@/components/chat/ToolsTooltip';

import { connect } from 'react-redux';
import { ErrorBoundary } from 'react-error-boundary';

type MessageFeedbackProps = {
  message: Message;
  currentUser: User | null;
};

export const MessageFeedbackComp = ({
  message,
  currentUser,
}: MessageFeedbackProps) => {
  const dispatch = useAppDispatch();
  const chatApi = getChatAPI();

  const updateReactionState = async (newReaction: Reaction | null) => {
    const oldMessage = message;
    const newMessage = { ...message, viewerReaction: newReaction };

    // Show new state immediately, assume request will succeed
    dispatch(updateMessage({ message: newMessage }));

    try {
      // Save state to server
      await chatApi.saveMessageReaction({
        messageId: newMessage.id,
        reaction: newMessage.viewerReaction,
      });
    } catch (e: any) {
      getLayoverLogger().logError(
        'Failed to update message reaction state:',
        e,
      );
      dispatch(updateMessage({ message: oldMessage }));
      dispatch(addError({ text: 'Error saving feedback. Please try again.' }));
    }
  };

  const onSubmitFeedback = async (feedback: string) => {
    if (!message.id) {
      getLayoverLogger().logError(
        `message not found in ChatStore [message.id=${message.id}]`,
      );
      return;
    }

    try {
      await chatApi.saveMessageFeedback({
        messageId: message.id,
        feedback,
      });
      dispatch(addSuccess({ text: 'Thanks for your feedback!' }));
    } catch (e: any) {
      getLayoverLogger().logError('Error saving message feedback:', e);
      dispatch(
        addError({
          text: 'Error submitting feedback. Please try again.',
        }),
      );
      // The error is caught here to be able to log specific issue
      // but thrown, because UI error handling is done in `FeedbackPopover.tsx`
      throw e;
    }
  };

  const isFromCurrentUser = message.sender.uuid === currentUser?.uuid;
  if (isFromCurrentUser) {
    // Don't show feedback buttons for messages from the current user
    return null;
  }
  const hasTools: boolean = message.hasToolCalls;

  return (
    <FeedbackButtons
      viewerReaction={message.viewerReaction}
      onUpdateReactionState={updateReactionState}
      onSubmitFeedback={onSubmitFeedback}
      heapPrefix={MESSAGE_FEEDBACK_PREFIX}
      heapProps={{ messageId: message.id }}
    >
      {hasTools && (
        <ErrorBoundary fallback={<span></span>}>
          <ToolsTooltip messageId={message.id} />
        </ErrorBoundary>
      )}
    </FeedbackButtons>
  );
};

const mapStateToProps = (state: RootState) => ({
  currentUser: state.userStore.currentUser,
});

export const MessageFeedback = connect(mapStateToProps)(MessageFeedbackComp);
